import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines,
  faRocket,
  faPenToSquare,
  faTrash,
  faDesktop,
  faMobileScreen,
  faLightbulb,
} from '@fortawesome/free-solid-svg-icons'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import { supabase } from '../../lib/supabase'
import { NUDGGE_API_BASE_URL } from '../../lib/config'
import Modal from '../shared/Modal'
import TemplateForm from './TemplateForm'

// Template List Component
function TemplateList({ selectedAccount }) {
  const navigate = useNavigate()
  const [deletingTemplate, setDeletingTemplate] = useState(null)

  const { data: templates, loading, error, refetch } = useSupabaseQuery('EmailTemplates', {
    eq: { IsDeleted: false, ...(selectedAccount && { AccountId: selectedAccount.AccountId }) },
    order: { column: 'Updated', ascending: false }
  })

  const { softDelete, loading: mutating } = useSupabaseMutation('EmailTemplates')

  const handleDelete = async () => {
    try {
      await softDelete(deletingTemplate.EmailTemplateId, 'EmailTemplateId')
      setDeletingTemplate(null)
      refetch()
    } catch (error) {
      console.error('Error deleting template:', error)
      alert('Failed to delete template')
    }
  }

  if (!selectedAccount) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Account Selected</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Please select an account from the Accounts tab first
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Email Templates</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Templates for: {selectedAccount.Name}
          </p>
        </div>
        <button
          onClick={() => navigate('new')}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Template</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {templates && templates.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Template ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {templates.map((template) => (
                  <tr
                    key={template.EmailTemplateId}
                    onClick={() => navigate(template.EmailTemplateId)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer"
                  >
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {template.Name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <code className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-200 px-2 py-1 rounded">
                        {template.TemplateIdentifier}
                      </code>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                        {template.SubjectLine || 'No subject'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {template.HtmlContent ? (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs rounded">
                          Published
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(template.Updated).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`edit/${template.EmailTemplateId}`)
                          }}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition"
                          title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeletingTemplate(template)
                          }}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No templates</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Email templates will appear here once created.</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingTemplate}
        onClose={() => setDeletingTemplate(null)}
        title="Delete Template"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete the template <strong>{deletingTemplate?.Name}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setDeletingTemplate(null)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={mutating}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {mutating ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// Template View Component
function TemplateView({ selectedAccount }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const [activeView, setActiveView] = useState('draft')
  const [deletingTemplate, setDeletingTemplate] = useState(null)

  const { data: templates, loading, refetch } = useSupabaseQuery('EmailTemplates', {
    eq: { EmailTemplateId: id, IsDeleted: false }
  })

  const { softDelete, loading: mutating } = useSupabaseMutation('EmailTemplates')

  const template = templates?.[0]

  const handleDelete = async () => {
    try {
      await softDelete(template.EmailTemplateId, 'EmailTemplateId')
      navigate('/service-email/templates')
    } catch (error) {
      console.error('Error deleting template:', error)
      alert('Failed to delete template')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Template not found</h3>
        <button
          onClick={() => navigate('/service-email/templates')}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Back to templates
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate('/service-email/templates')}
        className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span>Back to templates</span>
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{template.Name}</h2>
              <code className="text-xs text-gray-500 dark:text-gray-400">{template.TemplateIdentifier}</code>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveView('draft')}
                className={`px-4 py-2 text-sm rounded-lg transition ${
                  activeView === 'draft'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faFileLines} className="mr-1 w-3 h-3" fixedWidth />
                Draft
              </button>
              <button
                onClick={() => setActiveView('production')}
                className={`px-4 py-2 text-sm rounded-lg transition ${
                  activeView === 'production'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faRocket} className="mr-1 w-3 h-3" fixedWidth />
                Production
              </button>
              <button
                onClick={() => navigate(`/service-email/templates/edit/${template.EmailTemplateId}`)}
                className="px-4 py-2 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition"
              >
                <FontAwesomeIcon icon={faPenToSquare} className="mr-1 w-3 h-3" fixedWidth />
                Edit
              </button>
              <button
                onClick={() => setDeletingTemplate(template)}
                className="px-4 py-2 text-sm bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-1 w-3 h-3" fixedWidth />
                Delete
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subject Line
            </label>
            <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
              {template.SubjectLine || 'No subject'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {activeView === 'draft' ? 'Draft' : 'Production'} HTML Content
            </label>
            {(activeView === 'draft' ? template.DraftHtmlContent : template.HtmlContent) ? (
              <div className="relative">
                <pre className="text-xs text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-4 rounded overflow-x-auto max-h-96">
                  {activeView === 'draft' ? template.DraftHtmlContent : template.HtmlContent}
                </pre>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No {activeView} content available
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingTemplate}
        onClose={() => setDeletingTemplate(null)}
        title="Delete Template"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete the template <strong>{deletingTemplate?.Name}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setDeletingTemplate(null)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={mutating}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {mutating ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// Template Edit Component (Full Width)
function TemplateEdit({ selectedAccount }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const [formData, setFormData] = useState(null)
  const [isDraft, setIsDraft] = useState(true)
  const [showTestModal, setShowTestModal] = useState(false)
  const [rightPanel, setRightPanel] = useState(null) // null, 'preview', 'variables'
  const [previewMode, setPreviewMode] = useState('desktop') // 'desktop', 'mobile'

  const { data: templates, loading } = useSupabaseQuery('EmailTemplates', {
    eq: { EmailTemplateId: id, IsDeleted: false }
  })

  const { update, loading: mutating } = useSupabaseMutation('EmailTemplates')

  const template = templates?.[0]

  // Initialize form data when template loads
  useEffect(() => {
    if (template && !formData) {
      setFormData({
        Name: template.Name,
        SubjectLine: template.SubjectLine,
        HtmlContent: template.HtmlContent || template.DraftHtmlContent || '',
        TemplateIdentifier: template.TemplateIdentifier
      })
      setIsDraft(!!template.DraftHtmlContent && !template.HtmlContent)
    }
  }, [template, formData])

  // Extract variables from HTML content
  const extractVariables = (html) => {
    if (!html) return []
    const matches = html.match(/\{\{[^}]+\}\}/g) || []
    const uniqueVars = [...new Set(matches.map(m => m.replace(/[{}]/g, '')))]
    return uniqueVars
  }

  const handleUpdate = async () => {
    try {
      const payload = {
        Name: formData.Name,
        SubjectLine: formData.SubjectLine,
        IsDeleted: false
      }

      if (isDraft) {
        payload.DraftHtmlContent = formData.HtmlContent
        payload.HtmlContent = null
      } else {
        payload.HtmlContent = formData.HtmlContent
        payload.DraftHtmlContent = null
      }

      await update(id, payload, 'EmailTemplateId')
      navigate(`/service-email/templates/${id}`)
    } catch (error) {
      console.error('Error updating template:', error)
      alert('Failed to update template')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (!template || !formData) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Template not found</h3>
        <button
          onClick={() => navigate('/service-email/templates')}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Back to templates
        </button>
      </div>
    )
  }

  const variables = extractVariables(formData.HtmlContent)

  return (
    <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 pt-[120px] overflow-hidden">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 h-[120px] bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/service-email/templates/${id}`)}
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back</span>
              </button>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Template</h2>
            </div>
            <div className="flex items-center space-x-3">
              {/* Right Panel Toggles */}
              <button
                onClick={() => setRightPanel(rightPanel === 'preview' ? null : 'preview')}
                className={`px-3 py-2 text-sm rounded-lg transition flex items-center space-x-2 ${
                  rightPanel === 'preview'
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>Preview</span>
              </button>
              <button
                onClick={() => setRightPanel(rightPanel === 'variables' ? null : 'variables')}
                className={`px-3 py-2 text-sm rounded-lg transition flex items-center space-x-2 ${
                  rightPanel === 'variables'
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span>Variables ({variables.length})</span>
              </button>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={isDraft}
                  onChange={(e) => setIsDraft(e.target.checked)}
                  className="w-4 h-4 text-primary-500 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 focus:ring-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Save as draft</span>
              </label>
              <button
                onClick={() => setShowTestModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Send Test</span>
              </button>
              <button
                onClick={() => navigate(`/service-email/templates/${id}`)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={mutating}
                className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {mutating ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>

          {/* Template Info */}
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              value={formData.Name}
              onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Template Name"
            />
            <input
              type="text"
              value={formData.SubjectLine}
              onChange={(e) => setFormData({ ...formData, SubjectLine: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Subject Line"
            />
          </div>
        </div>
      </div>

      {/* Split Panel Layout */}
      <div className="h-[calc(100vh-120px)] flex">
        {/* Left: Editor */}
        <div className={`flex-1 transition-all duration-300 ${
          rightPanel ? 'mr-0' : 'mr-0'
        }`}>
          <div className="h-full p-6">
            <textarea
              value={formData.HtmlContent}
              onChange={(e) => setFormData({ ...formData, HtmlContent: e.target.value })}
              className="w-full h-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              placeholder="<html><body><h1>Hello {{name}}!</h1></body></html>"
            />
          </div>
        </div>

        {/* Right Drawer: Preview or Variables */}
        {rightPanel && (
          <div className="w-[600px] border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden flex flex-col">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {rightPanel === 'preview' ? 'Preview' : 'Variables'}
              </h3>
              <div className="flex items-center space-x-2">
                {rightPanel === 'preview' && (
                  <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={`px-3 py-1 text-xs rounded transition ${
                        previewMode === 'desktop'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <FontAwesomeIcon icon={faDesktop} className="mr-1 w-3 h-3" fixedWidth />
                      Desktop
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={`px-3 py-1 text-xs rounded transition ${
                        previewMode === 'mobile'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <FontAwesomeIcon icon={faMobileScreen} className="mr-1 w-3 h-3" fixedWidth />
                      Mobile
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setRightPanel(null)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-auto">
              {rightPanel === 'preview' && (
                <div className="h-full p-6 bg-gray-100 dark:bg-gray-950">
                  <div className={`mx-auto bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden ${
                    previewMode === 'mobile' ? 'max-w-[375px]' : 'w-full'
                  }`}>
                    <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        <strong>Subject:</strong> {formData.SubjectLine || 'No subject'}
                      </p>
                    </div>
                    <div className="p-4">
                      <iframe
                        srcDoc={formData.HtmlContent}
                        className={`w-full border-0 ${
                          previewMode === 'mobile' ? 'h-[667px]' : 'h-[700px]'
                        }`}
                        title="Email Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </div>
              )}

              {rightPanel === 'variables' && (
                <div className="p-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Variables detected in your template. Use these in your HTML with {`{{variable}}`} syntax.
                  </p>
                  {variables.length > 0 ? (
                    <div className="space-y-2">
                      {variables.map((variable, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                          <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-600">
                            {`{{${variable}}}`}
                          </code>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                      <p className="text-gray-500 dark:text-gray-400">No variables found</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        Add {`{{variable_name}}`} in your HTML
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Test Email Modal */}
      <TestEmailModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        template={formData}
        variables={variables}
        selectedAccount={selectedAccount}
      />
    </div>
  )
}

// Test Email Modal Component
function TestEmailModal({ isOpen, onClose, template, variables, selectedAccount }) {
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [testData, setTestData] = useState({
    toEmail: '',
    ...Object.fromEntries(variables.map(v => [v, '']))
  })

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSuccess(false)
      setError(null)
    }
  }, [isOpen])

  // Fetch API credentials for the selected account
  const { data: apiCredentials } = useSupabaseQuery('EmailApiCredentials', {
    eq: { IsDeleted: false, ...(selectedAccount && { AccountId: selectedAccount.AccountId }) },
    limit: 1
  })

  const handleSend = async () => {
    if (!testData.toEmail) {
      setError('Please enter a recipient email')
      return
    }

    if (!selectedAccount) {
      setError('No account selected')
      return
    }

    if (!apiCredentials || apiCredentials.length === 0) {
      setError('No API key found for this account. Please generate an API key in the API Keys tab first.')
      return
    }

    const apiKey = apiCredentials[0].Key

    setSending(true)
    try {
      // Replace variables in HTML content
      let html = template.HtmlContent
      Object.keys(testData).forEach(key => {
        if (key !== 'toEmail') {
          const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
          html = html.replace(regex, testData[key] || `{{${key}}}`)
        }
      })

      // Get Supabase session token for authorization
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Call Supabase Edge Function to send email
      const response = await fetch(`${NUDGGE_API_BASE_URL}/functions/v1/Send-Email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'X-Api-Key': apiKey
        },
        body: JSON.stringify({
          toEmail: testData.toEmail,
          subject: template.SubjectLine,
          message: html,
          templateIdentifier: template.TemplateIdentifier,
          accountId: selectedAccount.AccountId,
          accountName: selectedAccount.Name
        })
      })

      const result = await response.json()
      
      if (!response.ok) {
        console.error('Email send error:', result)
        throw new Error(result.error || result.message || 'Failed to send email')
      }

      console.log('Email sent successfully:', result)
      setSuccess(true)
      setError(null)
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Error sending test email:', error)
      const errorMessage = error.message || 'Unknown error occurred'
      setError(`Failed to send email: ${errorMessage}`)
      setSuccess(false)
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Test Email" size="lg">
      <div className="space-y-4">
        {/* Success Message */}
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  Email sent successfully!
                </h3>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  Test email has been sent to {testData.toEmail}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Failed to send email
                </h3>
                <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="flex-shrink-0 text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Recipient Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={testData.toEmail}
            onChange={(e) => setTestData({ ...testData, toEmail: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="test@example.com"
          />
        </div>

        {variables.length > 0 && (
          <>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Template Variables
              </h4>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {variables.map((variable) => (
                  <div key={variable}>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      <code className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">{`{{${variable}}}`}</code>
                    </label>
                    <input
                      type="text"
                      value={testData[variable] || ''}
                      onChange={(e) => setTestData({ ...testData, [variable]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={`Enter value for ${variable}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                <FontAwesomeIcon icon={faLightbulb} className="mr-1 w-3 h-3" fixedWidth />
                Tip: Fill in test values for all variables. Empty variables will remain as {`{{variable}}`} in the email.
              </p>
            </div>
          </>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || success || !testData.toEmail}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Send Test Email</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// Template New Component
function TemplateNew({ selectedAccount }) {
  const navigate = useNavigate()
  const { insert, loading: mutating } = useSupabaseMutation('EmailTemplates')

  const handleCreate = async (formData) => {
    try {
      await insert({ ...formData, AccountId: selectedAccount.AccountId })
      navigate('/service-email/templates')
    } catch (error) {
      console.error('Error creating template:', error)
      alert('Failed to create template')
    }
  }

  if (!selectedAccount) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">No Account Selected</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Please select an account before creating a template
        </p>
        <button
          onClick={() => navigate('/service-email/accounts')}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Go to Accounts
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate('/service-email/templates')}
        className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span>Back to templates</span>
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Create New Template</h2>
        <TemplateForm
          onSubmit={handleCreate}
          onCancel={() => navigate('/service-email/templates')}
          loading={mutating}
        />
      </div>
    </div>
  )
}

// Main Router Component
export default function TemplatesTab({ selectedAccount }) {
  return (
    <Routes>
      <Route index element={<TemplateList selectedAccount={selectedAccount} />} />
      <Route path=":id" element={<TemplateView selectedAccount={selectedAccount} />} />
      <Route path="edit/:id" element={<TemplateEdit selectedAccount={selectedAccount} />} />
      <Route path="new" element={<TemplateNew selectedAccount={selectedAccount} />} />
    </Routes>
  )
}
